import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import can from 'ember-can/helpers/can';
import cannot from 'ember-can/helpers/cannot';
import { dropTask, task, type TaskInstance } from 'ember-concurrency';
import InfinityLoader from 'ember-infinity/components/infinity-loader';
import type InfinityService from 'ember-infinity/services/infinity';
import { pluralize } from 'ember-inflector';
import PowerSelect from 'ember-power-select/components/power-select';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import EmberTooltip from 'ember-tooltips/components/ember-tooltip';
import { and, eq, not, or } from 'ember-truth-helpers';
import GlobalFlowIcon from 'garaje/components/global-flow-icon';
import TruncateWithTooltip from 'garaje/components/truncate-with-tooltip';
import LoadingSpinnerPulse from 'garaje/components/ui/classic/loading-spinner-pulse';
import MessageBox from 'garaje/components/ui/classic/message-box';
import ModalDialog from 'garaje/components/ui/classic/modal-dialog';
import SettingsPanel from 'garaje/components/ui/classic/settings-panel';
import UiButton from 'garaje/components/ui-button';
import UiInputField from 'garaje/components/ui-input-field';
import ExtendedInfinityModel, { type PaginatedRecordArray } from 'garaje/infinity-models/v3-offset';
import type FieldSetting from 'garaje/models/field-setting';
import type FlowModel from 'garaje/models/flow';
import type Location from 'garaje/models/location';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type FlashMessagesService from 'garaje/services/flash-messages';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import urlBuilder from 'garaje/utils/url-builder';
import { TrackedMap, TrackedSet } from 'tracked-built-ins';

interface RetentionOption {
  value: number | 'custom';
  label: string;
}

const RETENTION_OPTIONS: RetentionOption[] = [
  { value: 30, label: '30 days after visit date' },
  { value: 90, label: '90 days after visit date' },
  { value: 180, label: '180 days after visit date' },
  { value: 365, label: '1 year after visit date' },
  { value: 'custom', label: 'Custom' },
];

const PRESET_VALUES = new Set([30, 90, 180, 365]);

interface EntryRetentionPeriodSignature {
  Args: {
    currentLocation: Location;
  };
}

export default class EntryRetentionPeriod extends Component<EntryRetentionPeriodSignature> {
  @service declare flashMessages: FlashMessagesService;
  @service declare infinity: InfinityService;
  @service declare store: Store;
  @service declare ajaxFetch: AjaxFetchService;

  @tracked isOpen = false;
  @tracked selectedOption: RetentionOption | null = null;
  @tracked customDays = '';

  @tracked flows: PaginatedRecordArray<FlowModel> | null = null;
  openFlowIds = new TrackedSet<string>();

  @tracked showDeleteModal = false;
  @tracked confirmDeleteText = '';

  @tracked fieldSettingsByFlow = new TrackedMap<string, FieldSetting[]>();

  performedTaskInstances = new TrackedMap<string, TaskInstance<void>>();

  isFlowOpen = (flowId: string): boolean => {
    return this.openFlowIds.has(flowId);
  };

  isFlowLoading = (flowId: string): boolean => {
    // eslint-disable-next-line ember/use-ember-get-and-set
    const instance = this.performedTaskInstances.get(flowId);
    return Boolean(instance?.isRunning);
  };

  fieldSettingsFor = (flow: FlowModel): FieldSetting[] => {
    // eslint-disable-next-line ember/use-ember-get-and-set
    return this.fieldSettingsByFlow.get(flow.id) ?? [];
  };

  get customDaysIsNaN(): boolean {
    if (!this.isCustomSelected) return false;
    return isNaN(parseInt(this.customDays, 10));
  }

  get customDaysTooLow(): boolean {
    if (!this.isCustomSelected) return false;
    const days = parseInt(this.customDays, 10);
    return !isNaN(days) && days <= 0;
  }

  constructor(owner: unknown, args: EntryRetentionPeriodSignature['Args']) {
    super(owner, args);
    this.#initFromLocation();

    void this.loadFlowsTask.perform();
  }

  get errors(): string[] {
    const errors: string[] = [];
    if (this.customDaysIsNaN) {
      errors.push('Please enter a valid number');
    }
    if (this.customDaysTooLow) {
      errors.push('Retention period must be at least 1 day');
    }

    return errors;
  }

  get saveDisabled(): boolean {
    const isPristine = !this.hasDirtyFieldSettings && !this.args.currentLocation.hasDirtyAttributes;
    return this.saveTask.isRunning || Boolean(this.errors.length) || isPristine || this.showDeleteModal;
  }

  get indefinitely(): boolean {
    return !this.args.currentLocation.entryRetentionPeriod;
  }

  get isCustomSelected(): boolean {
    return this.selectedOption?.value === 'custom';
  }

  get retentionOptions(): RetentionOption[] {
    return RETENTION_OPTIONS;
  }

  get flowOptions(): FlowModel[] {
    return this.flows?.slice() ?? [];
  }

  get lastFlowIndex(): number {
    return this.flowOptions.length - 1;
  }

  get fieldSettingsHidden(): boolean {
    return this.indefinitely || this.args.currentLocation.redactAllVisitorData;
  }

  get hasDirtyFieldSettings(): boolean {
    if (this.fieldSettingsHidden) return false;

    for (const settings of this.fieldSettingsByFlow.values()) {
      if (settings.some((fs) => fs.hasDirtyAttributes)) return true;
    }
    return false;
  }

  get dirtySelectedFieldsCount(): number {
    if (this.fieldSettingsHidden) return 0;

    let count = 0;
    this.fieldSettingsByFlow.forEach((settings) => {
      settings.forEach((fs) => {
        if (<boolean>(<unknown>fs.hasDirtyAttributes) && fs.isSelected) count++;
      });
    });
    return count;
  }

  get dirtySelectedFlowsCount(): number {
    if (this.fieldSettingsHidden) return 0;

    let count = 0;
    this.fieldSettingsByFlow.forEach((settings) => {
      if (settings.some((fs) => <boolean>(<unknown>fs.hasDirtyAttributes) && fs.isSelected)) count++;
    });
    return count;
  }

  get retentionPeriodChanged(): boolean {
    if (!(<boolean>(<unknown>this.args.currentLocation.hasDirtyAttributes))) return false;
    const changed = this.args.currentLocation.changedAttributes();
    return 'entryRetentionPeriod' in changed || 'redactAllVisitorData' in changed;
  }

  get showWarningBanner(): boolean {
    const hasDirtyFields = this.dirtySelectedFieldsCount > 0;
    return hasDirtyFields || this.retentionPeriodChanged;
  }

  open = (): void => {
    this.isOpen = true;
  };

  cancel = (): void => {
    this.args.currentLocation.rollbackAttributes();
    this.#rollbackFieldSettings();
    this.#initFromLocation();
    this.isOpen = false;
  };

  setIndefinitely = (): void => {
    this.args.currentLocation.entryRetentionPeriod = null;
    this.selectedOption = null;
  };

  setRedactAll = (): void => {
    this.args.currentLocation.redactAllVisitorData = true;
  };

  setSelectFields = (): void => {
    this.args.currentLocation.redactAllVisitorData = false;
  };

  setCustomPeriod = (): void => {
    const firstPreset = RETENTION_OPTIONS[0]!;
    this.selectedOption = firstPreset;
    this.args.currentLocation.entryRetentionPeriod = firstPreset.value as number;
    if (!this.flows) {
      void this.loadFlowsTask.perform();
    }
  };

  selectOption = (option: RetentionOption): void => {
    this.selectedOption = option;
    this.customDays = String(this.args.currentLocation.entryRetentionPeriod);

    if (option.value !== 'custom') {
      this.args.currentLocation.entryRetentionPeriod = option.value;
    } else {
      const days = parseInt(this.customDays, 10);
      this.args.currentLocation.entryRetentionPeriod = !isNaN(days) && days > 0 ? days : 30;
    }
  };

  updateCustomDays = (value: string): void => {
    this.customDays = value;
    const days = parseInt(value, 10);

    if (!this.customDaysIsNaN && !this.customDaysTooLow) {
      this.args.currentLocation.entryRetentionPeriod = days;
    }
  };

  toggleAccordion = (flow: FlowModel): void => {
    if (this.openFlowIds.has(flow.id)) {
      this.openFlowIds.delete(flow.id);
      return;
    }
    this.openFlowIds.add(flow.id);
    if (!this.fieldSettingsByFlow.has(flow.id) && !this.isFlowLoading(flow.id)) {
      this.#performLoadFieldSettings(flow);
    }
  };

  #performLoadFieldSettings(flow: FlowModel): void {
    const instance = this.loadFieldSettingsTask.perform(flow);
    // eslint-disable-next-line ember/use-ember-get-and-set
    this.performedTaskInstances.set(flow.id, instance);
  }

  toggleFieldSetting = (fieldSetting: FieldSetting): void => {
    fieldSetting.isSelected = !fieldSetting.isSelected;
  };

  selectAllFields = (flow: FlowModel): void => {
    // eslint-disable-next-line ember/use-ember-get-and-set
    const settings = this.fieldSettingsByFlow.get(flow.id) ?? [];
    settings.forEach((fs) => (fs.isSelected = true));
  };

  deselectAllFields = (flow: FlowModel): void => {
    // eslint-disable-next-line ember/use-ember-get-and-set
    const settings = this.fieldSettingsByFlow.get(flow.id) ?? [];
    settings.forEach((fs) => (fs.isSelected = false));
  };

  handleSaveClick = (): void => {
    const wouldDelete =
      this.#hasNewlySelectedFields() || this.#retentionPeriodShrunk() || this.#redactAllNewlyEnabled();

    if (wouldDelete) {
      this.confirmDeleteText = '';
      this.showDeleteModal = true;
    } else {
      void this.saveTask.perform();
    }
  };

  cancelDeleteModal = (): void => {
    this.confirmDeleteText = '';
    this.showDeleteModal = false;
  };

  updateConfirmDeleteText = (value: string): void => {
    this.confirmDeleteText = value;
  };

  loadFlowsTask = dropTask(async () => {
    this.flows = await this.infinity.model(
      'flow',
      {
        filter: {
          location: this.args.currentLocation.id,
          include_deleted: true,
          // include employee-centric and property-owned flows which are not included unless explicitly requested
          employee_centric: 'true,false',
          property_owned: 'true,false',
        },
        perPage: 20,
        startingPage: 0,
        perPageParam: 'page[limit]',
        pageParam: 'page[offset]',
        countParam: 'meta.total',
      },
      ExtendedInfinityModel,
    );

    const firstFlow = this.flows.slice()[0];
    if (firstFlow) {
      this.openFlowIds.add(firstFlow.id);
      this.#performLoadFieldSettings(firstFlow);
    }
  });

  loadFieldSettingsTask = task(async (flow: FlowModel) => {
    let fieldSettings = (<FieldSetting[] | null>flow.hasMany('fieldSettings').value())?.slice();
    const hasChanges = fieldSettings?.some((fs) => fs.hasDirtyAttributes);

    if (hasChanges) {
      fieldSettings = (await flow.fieldSettings)?.slice() ?? [];
    } else {
      fieldSettings = (await flow.fieldSettings.reload())?.slice() ?? [];
    }
    // eslint-disable-next-line ember/use-ember-get-and-set
    this.fieldSettingsByFlow.set(flow.id, fieldSettings);
  });

  saveTask = dropTask(async () => {
    const changes = <[number | null, number | null] | undefined>(
      this.args.currentLocation.changedAttributes()['entryRetentionPeriod']
    );
    const [oldRetention, newRetention] = changes ?? [null, null];

    const redactChanges = <[boolean, boolean] | undefined>(
      this.args.currentLocation.changedAttributes()['redactAllVisitorData']
    );
    const [oldRedactAll, newRedactAll] = redactChanges ?? [null, null];

    const { currentLocation } = this.args;
    const flows = this.flows?.slice() ?? [];

    if (<boolean>(<unknown>currentLocation.hasDirtyAttributes)) {
      try {
        await currentLocation.save();
      } catch (e) {
        this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
        return;
      }
    }

    const serialized = flows
      .map((flow) => flow.serializeDirtyFieldSettings())
      .filter((s): s is NonNullable<typeof s> => s !== null);

    // only send PATCH request if there are dirty field settings and we are not setting to indefinitely or discarding all data
    if (serialized.length && !this.indefinitely && !this.args.currentLocation.redactAllVisitorData) {
      try {
        await this.ajaxFetch.request(urlBuilder.v2.fieldSettings(), {
          type: 'PATCH',
          contentType: 'application/json',
          data: JSON.stringify({ field_settings: serialized }),
        });
      } catch (e) {
        if (changes || redactChanges) {
          // rollback location changes if the request fails, since the field settings won't be updated
          if (changes) this.args.currentLocation.entryRetentionPeriod = oldRetention;
          if (redactChanges) this.args.currentLocation.redactAllVisitorData = <boolean>oldRedactAll;
          let rollbackSucceeded = true;
          try {
            await currentLocation.save();
          } catch {
            // rollback save also failed: server is still at the NEW values from the earlier
            // successful save, but local stays at OLD so the form matches the user's pre-edit
            // intent. Divergence resolves on next refresh when canonical reloads from server.
            rollbackSucceeded = false;
          }
          if (rollbackSucceeded) {
            // restore form state to the user's intended values so they can retry the save
            if (changes) this.args.currentLocation.entryRetentionPeriod = newRetention;
            if (redactChanges) this.args.currentLocation.redactAllVisitorData = <boolean>newRedactAll;
          }
        }
        this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
        return;
      }
    }

    // these are reloaded every time into the store and not serialized from the PATCH
    this.#rollbackFieldSettings();
    this.#initFromLocation();

    this.isOpen = false;
    this.confirmDeleteText = '';
    this.showDeleteModal = false;

    this.flashMessages.showAndHideFlash('success', 'Data retention policy updated');
  });

  #rollbackFieldSettings() {
    this.fieldSettingsByFlow.forEach((settings) => {
      settings.forEach((setting) => {
        setting.rollbackAttributes();
      });
    });

    this.fieldSettingsByFlow.clear();
    this.performedTaskInstances.clear();
    this.openFlowIds.clear();

    // on reset, just the first flow is opened and its field settings are loaded
    const firstFlow = this.flows?.slice()[0];
    if (firstFlow) {
      this.openFlowIds.add(firstFlow.id);
      this.#performLoadFieldSettings(firstFlow);
    }
  }

  #initFromLocation(): void {
    this.customDays = '';

    const days = this.args.currentLocation.entryRetentionPeriod;
    if (!days) {
      this.selectedOption = null;
      return;
    }
    if (PRESET_VALUES.has(days)) {
      this.selectedOption = RETENTION_OPTIONS.find((o) => o.value === days) ?? null;
    } else {
      this.selectedOption = RETENTION_OPTIONS.find((o) => o.value === 'custom') ?? null;
      this.customDays = String(days);
    }
  }

  #hasNewlySelectedFields() {
    if (this.indefinitely) return false;

    for (const settings of this.fieldSettingsByFlow.values()) {
      if (settings.some((fs) => <boolean>(<unknown>fs.hasDirtyAttributes) && fs.isSelected)) return true;
    }

    return false;
  }

  #redactAllNewlyEnabled(): boolean {
    if (this.indefinitely) return false;

    const change = <[boolean, boolean] | undefined>(
      this.args.currentLocation.changedAttributes()['redactAllVisitorData']
    );

    if (!change) return false;

    const [oldValue, newValue] = change;
    return !oldValue && newValue;
  }

  #retentionPeriodShrunk() {
    const change = <[number | null, number | null] | undefined>(
      this.args.currentLocation.changedAttributes()['entryRetentionPeriod']
    );

    if (!change) return false;

    const [oldPeriod, newPeriod] = change;

    // if new period is indefinitely, it can't be shrinking
    if (!newPeriod) return false;

    // if old period is indefinitely, any new period would be shrinking
    if (!oldPeriod) return true;

    // compare the two periods and see if it's shrinking
    return newPeriod < oldPeriod;
  }

  <template>
    <SettingsPanel @isOpen={{this.isOpen}} data-test-entry-retention-period as |panel|>
      <panel.header
        @iconUri="/assets/images/features/data-retention.svg"
        @title="Visitor data retention policy"
        @onCancel={{this.cancel}}
        as |header|
      >
        <header.description>
          Specify how long visitor data is retained after a visit.<br />
          Learn more about managing
          <a
            href="https://envoy.help/en/articles/12356182-visitor-data-anonymization"
            target="_blank"
            rel="noopener"
          >data retention →</a>
        </header.description>

        <header.controls>
          {{#if this.isOpen}}
            <UiButton
              class="btn btn--small btn--brand"
              disabled={{this.saveDisabled}}
              {{on "click" this.handleSaveClick}}
              data-test-entry-retention-period-save
            >
              {{if this.saveTask.isRunning "Saving..." "Save"}}
            </UiButton>
          {{else if (can "update data-retention")}}
            <UiButton class="btn btn--small btn--brand" {{on "click" this.open}} data-test-entry-retention-period-edit>
              Edit
            </UiButton>
          {{/if}}
        </header.controls>
      </panel.header>
      <panel.body as |SPContent|>
        <SPContent>
          <p id="retention-period-heading" class="font-bold text-carbon-70 mb-4 mt-10">Retention period</p>
          <div class="space-y-4">
            <div class="space-y-4" role="radiogroup" aria-labelledby="retention-period-heading">
              <label class="flex items-center gap-2 cursor-pointer" data-test-retain-indefinitely>
                <input
                  type="radio"
                  name="entry-retention-policy"
                  class="custom-radio shrink-0"
                  checked={{this.indefinitely}}
                  {{on "change" this.setIndefinitely}}
                />
                <span class="text-sm">Retain data indefinitely</span>
              </label>
              <label class="flex items-center gap-2 cursor-pointer" data-test-retain-custom>
                <input
                  type="radio"
                  name="entry-retention-policy"
                  class="custom-radio shrink-0"
                  checked={{not this.indefinitely}}
                  {{on "change" this.setCustomPeriod}}
                />
                <span class="text-sm">Discard data after a set retention period</span>
              </label>
            </div>
            {{#unless this.indefinitely}}
              <div class="flex">
                <UiInputField data-test-retention-period-select @required={{true}} as |Field|>
                  <Field.label>Retain for</Field.label>

                  <PowerSelect
                    @options={{this.retentionOptions}}
                    @selected={{this.selectedOption}}
                    @onChange={{this.selectOption}}
                    @searchEnabled={{false}}
                    as |option|
                  >
                    {{option.label}}
                  </PowerSelect>
                </UiInputField>
                {{#if this.isCustomSelected}}
                  <UiInputField
                    class="ml-4 mt-[22px]"
                    @min="1"
                    @step="1"
                    @type="number"
                    @errors={{this.errors}}
                    data-test-retention-custom-input
                    as |Field|
                  >
                    <div class="flex items-center">
                      <Field.input
                        class="!w-16"
                        @value={{this.customDays}}
                        @update={{this.updateCustomDays}}
                        data-test-retention-days-input
                      />
                      {{#if @currentLocation.entryRetentionPeriod}}
                        <span class="text-sm text-carbon-70 whitespace-nowrap ml-2">{{pluralize
                            @currentLocation.entryRetentionPeriod
                            "day"
                            withoutCount=true
                          }}
                          after visit date</span>
                      {{/if}}
                    </div>
                  </UiInputField>
                {{/if}}
              </div>

              <div class="mt-8" data-test-data-to-discard>
                <p id="data-to-discard-heading" class="font-bold mb-1 text-carbon-70">Data to discard</p>
                <div class="space-y-4 mb-4" role="radiogroup" aria-labelledby="data-to-discard-heading">
                  <label class="flex items-center gap-2 cursor-pointer" data-test-redact-all-data>
                    <input
                      type="radio"
                      name="redact-all-visitor-data"
                      class="custom-radio shrink-0"
                      checked={{or
                        @currentLocation.redactAllVisitorData
                        (cannot "update field settings data-retention")
                      }}
                      {{on "change" this.setRedactAll}}
                    />
                    <span class="text-sm">Discard all data by default</span>
                  </label>
                  <label
                    class="flex items-center gap-2
                      {{if (cannot 'update field settings data-retention') '!cursor-not-allowed' 'cursor-pointer'}}"
                    data-test-redact-select-fields
                  >
                    <input
                      type="radio"
                      name="redact-all-visitor-data"
                      class="custom-radio shrink-0"
                      checked={{and
                        (not @currentLocation.redactAllVisitorData)
                        (can "update field settings data-retention")
                      }}
                      disabled={{cannot "update field settings data-retention"}}
                      {{on "change" this.setSelectFields}}
                    />
                    <span class="text-sm">Select which fields to discard after set retention period</span>
                    {{#if (cannot "update field settings data-retention")}}
                      <span data-test-redact-select-fields-locked>
                        {{svgJar "lock-gray"}}
                        <EmberTooltip
                          @text="Requires Visitors Enterprise"
                          @side="top"
                          @arrowClass="hidden"
                          @popperContainer="body"
                        />
                      </span>
                    {{/if}}
                  </label>
                </div>

                {{#if (can "update field settings data-retention")}}
                  {{#unless @currentLocation.redactAllVisitorData}}
                    <div class="mt-4" data-test-flow-accordions>
                      {{#each this.flowOptions as |flow index|}}
                        <div
                          class="border-x border-t
                            {{if (eq index 0) 'rounded-t-md'}}
                            {{if (eq index this.lastFlowIndex) 'rounded-b-md border-b'}}"
                          data-test-flow-accordion={{flow.name}}
                        >
                          <button
                            type="button"
                            class="w-full flex items-center py-3 px-3 bg-carbon-5 hover:bg-carbon-10 text-left
                              {{if (eq index 0) 'rounded-t-md'}}
                              {{if (and (eq index this.lastFlowIndex) (not (this.isFlowOpen flow.id))) 'rounded-b-md'}}"
                            aria-expanded="{{this.isFlowOpen flow.id}}"
                            aria-controls="flow-accordion-body-{{flow.id}}"
                            {{on "click" (fn this.toggleAccordion flow)}}
                            data-test-flow-accordion-toggle
                          >
                            {{#if (this.isFlowOpen flow.id)}}
                              {{svgJar "chevron-up" height="10" width="10" class="mr-2 shrink-0"}}
                            {{else}}
                              {{svgJar "chevron-down" height="10" width="10" class="mr-2 shrink-0"}}
                            {{/if}}
                            <span class="flex items-center text-sm">
                              <GlobalFlowIcon @show={{flow.isGlobalChild}}>
                                {{flow.name}}
                              </GlobalFlowIcon>
                              {{#if flow.deletedAt}}
                                <span class="ml-1">(Deleted)</span>
                              {{/if}}
                            </span>
                          </button>

                          {{#if (this.isFlowOpen flow.id)}}
                            <div id="flow-accordion-body-{{flow.id}}" class="px-3 py-2" data-test-flow-accordion-body>
                              {{#if (this.isFlowLoading flow.id)}}
                                <div class="relative min-h-32">
                                  <LoadingSpinnerPulse data-test-field-settings-loading />
                                </div>
                              {{else}}
                                {{#let (this.fieldSettingsFor flow) as |fieldSettings|}}
                                  {{#if fieldSettings.length}}
                                    <div data-test-field-settings-list>
                                      <div class="flex gap-1 text-sm mb-2">
                                        <UiButton
                                          {{on "click" (fn this.selectAllFields flow)}}
                                          class="text-gem-50"
                                          data-test-select-all-fields
                                        >
                                          Select all
                                        </UiButton>
                                        <span class="text-carbon-50 font-bold">|</span>
                                        <UiButton
                                          {{on "click" (fn this.deselectAllFields flow)}}
                                          class="text-gem-50"
                                          data-test-deselect-all-fields
                                        >
                                          Deselect all
                                        </UiButton>
                                      </div>
                                      {{#each fieldSettings as |fieldSetting|}}
                                        <div class="flex flex-col py-1 w-fit" data-test-field-setting>
                                          <label class="flex items-center gap-2 cursor-pointer">
                                            <input
                                              type="checkbox"
                                              class="shrink-0"
                                              checked={{fieldSetting.isSelected}}
                                              {{on "change" (fn this.toggleFieldSetting fieldSetting)}}
                                            />
                                            <div class="text-sm flex items-center">
                                              {{#if fieldSetting.isAttachment}}
                                                {{svgJar "attachment" class="mr-1"}}
                                              {{/if}}
                                              <TruncateWithTooltip @value={{fieldSetting.displayName}} @length={{70}} />
                                            </div>
                                          </label>
                                          {{#if fieldSetting.flowVersionId}}
                                            <span class="text-xs text-carbon-50 mt-1" data-test-field-setting-version>
                                              Field added by an
                                              <a
                                                href="https://envoy.help/en/articles/13764979-advanced-visitor-approvals#h_633158c5eb"
                                                target="_blank"
                                                rel="noopener"
                                              >
                                                Approval Policy
                                              </a>.
                                            </span>
                                          {{/if}}
                                        </div>
                                      {{/each}}
                                    </div>
                                  {{/if}}
                                {{/let}}
                              {{/if}}
                            </div>
                          {{/if}}
                        </div>
                      {{/each}}

                      {{#if this.flows.canLoadMore}}
                        <div class="infinity-loading-log !text-carbon-50 mt-4">
                          <InfinityLoader @infinityModel={{this.flows}} @hideOnInfinity={{true}}>
                            <img alt="loading" src="/assets/images/loading-grey.gif" />
                            <span class="loading-text">Loading…</span>
                          </InfinityLoader>
                        </div>
                      {{/if}}
                    </div>
                  {{/unless}}
                {{/if}}
              </div>
            {{/unless}}
          </div>
        </SPContent>
      </panel.body>
    </SettingsPanel>

    {{#if this.showDeleteModal}}
      <ModalDialog
        @clickOutsideToClose={{false}}
        @translucentOverlay={{true}}
        @onClose={{this.cancelDeleteModal}}
        @targetAttachment="center"
        @containerClass="ember-modal-dialog !max-w-[612px]"
      >
        <div class="p-8" data-test-field-settings-delete-modal>
          <h1 class="text-2xl mb-4">Confirm retention policy</h1>
          <p class="text-sm text-carbon-60">
            This selection will apply to past, recent, and future visit data.
          </p>

          <ul class="mb-6 text-sm list-disc ml-4">
            {{#if @currentLocation.entryRetentionPeriod}}
              <li>
                <span class="text-carbon-60">
                  Data in these fields will be deleted
                  {{pluralize @currentLocation.entryRetentionPeriod "day"}}
                  after the visit date.
                </span>
              </li>
            {{/if}}
            {{#if @currentLocation.entryRetentionPeriod}}
              <li>
                <span class="text-papaya-50">
                  Data older than
                  {{pluralize @currentLocation.entryRetentionPeriod "day"}}
                  will be deleted immediately.
                </span>
              </li>
            {{/if}}
          </ul>

          <p class="text-sm text-carbon-60 mb-6">
            Deleted data cannot be recovered, including by Envoy’s support team.
          </p>
          {{#if this.showWarningBanner}}
            <MessageBox @type="warning" class="mb-4" data-test-delete-confirmation-message>
              {{#if this.dirtySelectedFieldsCount}}
                This action will permanently delete
                {{pluralize this.dirtySelectedFieldsCount "newly selected sign in field"}}
                across
                {{pluralize this.dirtySelectedFlowsCount "sign in flow"}}.
              {{/if}}
              Fields already scheduled to be discarded at a future date may be permanently discarded immediately if past
              the new retention period.
            </MessageBox>
          {{/if}}

          <UiInputField @required={{true}} data-test-delete-confirmation-input as |Field|>
            <Field.label>Type
              <strong>delete</strong>
              (lowercase) to confirm</Field.label>
            <Field.input @value={{this.confirmDeleteText}} @update={{this.updateConfirmDeleteText}} />
          </UiInputField>
          <div class="flex justify-end gap-3 mt-6">
            <UiButton
              class="btn btn--small btn--nonHoverFill"
              disabled={{this.saveTask.isRunning}}
              data-test-delete-modal-cancel
              {{on "click" this.cancelDeleteModal}}
            >
              Cancel
            </UiButton>
            <UiButton
              class="btn btn--small btn--brand"
              disabled={{or (not (eq this.confirmDeleteText "delete")) this.saveTask.isRunning}}
              data-test-delete-modal-confirm
              {{on "click" this.saveTask.perform}}
            >
              {{if this.saveTask.isRunning "Saving..." "Confirm and delete"}}
            </UiButton>
          </div>
        </div>
      </ModalDialog>
    {{/if}}
  </template>
}
